import type { BacktestMetrics, Candle } from '../../domain/types'
import {
  DEFAULT_COSTS,
  DEFAULT_STOCK_PARAMS,
  generateSignals,
  simulateSignals,
  summarize,
  type CostModel,
  type StockStrategyParams,
  type StockStrategyType,
} from './backtest'
import { mean } from './statistics'

export type Objective = 'return' | 'sharpe' | 'calmar'

export function runMetrics(candles: readonly Candle[], params: StockStrategyParams, costs: CostModel = DEFAULT_COSTS): BacktestMetrics {
  const sim = simulateSignals(candles, generateSignals(candles, params), costs)
  return summarize(sim.equityCurve, sim.tradePnls, sim.stepReturns)
}

export function objectiveValue(m: BacktestMetrics, objective: Objective): number {
  if (objective === 'sharpe') return m.sharpeLike
  if (objective === 'calmar') return m.maxDrawdownPct > 0.5 ? m.totalReturnPct / m.maxDrawdownPct : m.totalReturnPct
  return m.totalReturnPct
}

export interface GridEntry {
  params: StockStrategyParams
  metric: number
  metrics: BacktestMetrics
}

export function paramGrid(type: StockStrategyType): StockStrategyParams[] {
  const base = DEFAULT_STOCK_PARAMS
  const out: StockStrategyParams[] = []
  if (type === 'sma-cross') {
    for (const fast of [5, 10, 15, 20, 30]) for (const slow of [30, 50, 80, 120]) if (fast < slow) out.push({ ...base, type, fast, slow })
  } else if (type === 'rsi-reversion') {
    for (const rsiPeriod of [7, 14, 21]) for (const rsiLow of [20, 25, 30, 35]) for (const rsiHigh of [65, 70, 75, 80]) out.push({ ...base, type, rsiPeriod, rsiLow, rsiHigh })
  } else {
    for (const momentumPeriod of [10, 20, 40, 60]) for (const momentumThreshold of [0, 0.01, 0.02, 0.05]) out.push({ ...base, type, momentumPeriod, momentumThreshold })
  }
  return out
}

export function paramAxes(type: StockStrategyType): { x: { key: keyof StockStrategyParams; values: number[] }; y: { key: keyof StockStrategyParams; values: number[] } } {
  if (type === 'sma-cross') return { x: { key: 'fast', values: [5, 10, 15, 20, 30] }, y: { key: 'slow', values: [30, 50, 80, 120] } }
  if (type === 'rsi-reversion') return { x: { key: 'rsiLow', values: [20, 25, 30, 35] }, y: { key: 'rsiHigh', values: [65, 70, 75, 80] } }
  return { x: { key: 'momentumPeriod', values: [10, 20, 40, 60] }, y: { key: 'momentumThreshold', values: [0, 0.01, 0.02, 0.05] } }
}

export function paramsToRecord(params: StockStrategyParams): Record<string, number> {
  if (params.type === 'sma-cross') return { fast: params.fast, slow: params.slow }
  if (params.type === 'rsi-reversion') return { rsiPeriod: params.rsiPeriod, rsiLow: params.rsiLow, rsiHigh: params.rsiHigh }
  return { momentumPeriod: params.momentumPeriod, momentumThreshold: params.momentumThreshold }
}

export function evaluateGrid(
  candles: readonly Candle[],
  type: StockStrategyType,
  costs: CostModel = DEFAULT_COSTS,
  objective: Objective = 'return',
  minTrades = 3,
): GridEntry[] {
  return paramGrid(type).map((params) => {
    const metrics = runMetrics(candles, params, costs)
    const metric = metrics.trades < minTrades ? Number.NEGATIVE_INFINITY : objectiveValue(metrics, objective)
    return { params, metric, metrics }
  })
}

export function bestOf(grid: readonly GridEntry[]): GridEntry {
  return grid.reduce((a, b) => (b.metric > a.metric ? b : a), grid[0])
}

export interface TrainTestResult {
  bestParams: StockStrategyParams
  train: BacktestMetrics
  test: BacktestMetrics
  degradationPct: number
  nConfigs: number
  trainGrid: GridEntry[]
}

// Optimize on the training window, then measure the chosen config on the untouched test window.
export function trainTestSplit(
  candles: readonly Candle[],
  type: StockStrategyType,
  costs: CostModel = DEFAULT_COSTS,
  objective: Objective = 'return',
  splitRatio = 0.7,
): TrainTestResult {
  const splitIdx = Math.floor(candles.length * splitRatio)
  const train = candles.slice(0, splitIdx)
  const test = candles.slice(splitIdx)
  const grid = evaluateGrid(train, type, costs, objective)
  const best = bestOf(grid)
  const trainM = best.metrics
  const testM = runMetrics(test, best.params, costs)
  const degradationPct =
    Math.abs(trainM.totalReturnPct) > 1e-9 ? ((trainM.totalReturnPct - testM.totalReturnPct) / Math.abs(trainM.totalReturnPct)) * 100 : 0
  return { bestParams: best.params, train: trainM, test: testM, degradationPct, nConfigs: grid.length, trainGrid: grid }
}

export interface WalkForwardFold {
  fold: number
  trainReturnPct: number
  testReturnPct: number
  testMaxDrawdownPct: number
  trades: number
  bestParams: Record<string, number>
}

export interface WalkForwardOptimizationResult {
  folds: WalkForwardFold[]
  oosReturnPct: number
  isReturnPct: number
  efficiency: number
  consistency: number
  insufficient: boolean
}

// Anchored walk-forward: each fold re-optimizes on all prior data, then trades the next unseen window.
export function walkForwardOptimize(
  candles: readonly Candle[],
  type: StockStrategyType,
  costs: CostModel = DEFAULT_COSTS,
  objective: Objective = 'return',
  folds = 4,
): WalkForwardOptimizationResult {
  const n = candles.length
  const block = Math.floor(n / (folds + 1))
  if (block < 20) return { folds: [], oosReturnPct: 0, isReturnPct: 0, efficiency: 0, consistency: 0, insufficient: true }

  const out: WalkForwardFold[] = []
  let oosGrowth = 1
  for (let f = 0; f < folds; f++) {
    const testStart = (f + 1) * block
    const testEnd = f === folds - 1 ? n : testStart + block
    const train = candles.slice(0, testStart)
    const test = candles.slice(testStart, testEnd)
    const best = bestOf(evaluateGrid(train, type, costs, objective))
    const testM = runMetrics(test, best.params, costs)
    oosGrowth *= 1 + testM.totalReturnPct / 100
    out.push({
      fold: f + 1,
      trainReturnPct: best.metrics.totalReturnPct,
      testReturnPct: testM.totalReturnPct,
      testMaxDrawdownPct: testM.maxDrawdownPct,
      trades: testM.trades,
      bestParams: paramsToRecord(best.params),
    })
  }

  const isReturnPct = mean(out.map((o) => o.trainReturnPct))
  const meanTest = mean(out.map((o) => o.testReturnPct))
  const oosReturnPct = (oosGrowth - 1) * 100
  const efficiency = isReturnPct > 1e-9 ? meanTest / isReturnPct : meanTest > 0 ? 1 : 0
  const consistency = out.length ? out.filter((o) => o.testReturnPct > 0).length / out.length : 0
  return { folds: out, oosReturnPct, isReturnPct, efficiency, consistency, insufficient: false }
}

export interface SegmentMatrix {
  configs: StockStrategyParams[]
  matrix: number[][]
  segments: number
}

// Performance of every config across S contiguous time segments — the raw input for PBO/CSCV.
export function gridSegmentMatrix(
  candles: readonly Candle[],
  type: StockStrategyType,
  segments: number,
  costs: CostModel = DEFAULT_COSTS,
  objective: Objective = 'return',
): SegmentMatrix {
  const configs = paramGrid(type)
  const size = Math.floor(candles.length / segments)
  const matrix: number[][] = configs.map(() => new Array<number>(segments).fill(0))
  for (let s = 0; s < segments; s++) {
    const start = s * size
    const end = s === segments - 1 ? candles.length : start + size
    const slice = candles.slice(start, end)
    configs.forEach((params, c) => {
      matrix[c][s] = objectiveValue(runMetrics(slice, params, costs), objective)
    })
  }
  return { configs, matrix, segments }
}
